import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dining-guide-3/artifacts/egfzykn3_image.png";

const EntryPopup = ({ onClose }) => {
  const { t } = useLanguage();

  const handlePlatformClick = (url) => {
    window.open(url, '_blank');
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        onClick={onClose}
        data-testid="entry-popup-overlay"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative w-full max-w-md bg-gradient-to-br from-hanse-dark via-hanse-navy to-hanse-dark rounded-2xl shadow-2xl border border-hanse-gold/30 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="entry-popup-card"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-10"
            data-testid="popup-close-btn"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Content */}
          <div className="p-6 sm:p-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <motion.img
                src={LOGO_URL}
                alt="Hanse Pizza Logo"
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-full shadow-lg border-2 border-hanse-gold/50"
                whileHover={{ rotate: 5 }}
              />
            </div>

            {/* Promo Badge */}
            <div className="flex justify-center mb-4">
              <span className="bg-hanse-red text-white px-4 py-1 rounded-full text-sm font-semibold animate-pulse">
                🔥 {t('popup.promo')}
              </span>
            </div>

            {/* Title */}
            <h2 className="text-xl sm:text-2xl font-heading font-bold text-white text-center mb-2">
              {t('popup.title')}
            </h2>
            <p className="text-white/70 text-center text-sm mb-6">
              {t('popup.subtitle')}
            </p>

            {/* Platform Buttons */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
              <Button
                onClick={() => handlePlatformClick('https://www.ubereats.com/de/store/hanse-pizza/cTNFBxdnR9mVVXQv3W28bg')}
                className="bg-[#06C167] hover:bg-[#05a858] text-white py-3 px-2 rounded-xl font-semibold text-xs sm:text-sm flex items-center justify-center gap-1"
                data-testid="popup-ubereats-btn"
              >
                🛵 {t('popup.uberEats')}
              </Button>
              <Button
                onClick={() => handlePlatformClick('https://www.lieferando.de')}
                className="bg-[#FF8000] hover:bg-[#e67300] text-white py-3 px-2 rounded-xl font-semibold text-xs sm:text-sm flex items-center justify-center gap-1"
                data-testid="popup-lieferando-btn"
              >
                🍕 {t('popup.lieferando')}
              </Button>
              <Button
                onClick={() => handlePlatformClick('https://wolt.com')}
                className="bg-[#009DE0] hover:bg-[#0088c4] text-white py-3 px-2 rounded-xl font-semibold text-xs sm:text-sm flex items-center justify-center gap-1"
                data-testid="popup-wolt-btn"
              >
                🌟 {t('popup.wolt')}
              </Button>
            </div>

            {/* Call Us & Order Button - Main CTA */}
            <Button
              onClick={() => window.location.href = 'tel:+4942148535145'}
              className="w-full bg-hanse-red hover:bg-hanse-red/90 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 mb-4 text-lg"
              data-testid="popup-call-btn"
            >
              <Phone className="w-6 h-6" />
              {t('popup.callUs')}
            </Button>

            {/* Phone Number */}
            <div className="text-center">
              <a
                href="tel:+4942148535145"
                className="text-hanse-gold font-bold text-xl hover:text-hanse-gold/80 transition-colors"
              >
                +49 421 48535145
              </a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EntryPopup;
